import {
  PARTS_CATALOG_AUTOCOMPLETE_API,
  PARTS_CATALOG_AUTOCOMPLETE_LIMIT,
  QUOTE_DOCUMENTS_API_BASE,
} from '@/constant/quoteDocuments';
import { QuoteDocumentActivityKind } from '@/types/quoteDocumentComments';
import {
  CreateQuoteDocumentVersionPayload,
  CreateQuoteDocumentVersionResult,
  ListQuoteDocumentActivityParams,
  ListQuoteDocumentsParams,
  PartsCatalogHit,
  PartsCatalogHitWire,
  PatchQuoteDocumentPayload,
  QuoteDocumentActivity,
  QuoteDocumentActivityListResponse,
  QuoteDocumentActivityWire,
  QuoteDocumentDetail,
  QuoteDocumentLifecyclePayload,
  QuoteDocumentRevisionsListResponse,
  QuoteDocumentsListResponse,
  ReplaceQuoteDocumentLinesPayload,
} from '@/types/quoteDocuments';
import {
  normalizeCreateQuoteDocumentVersionResult,
  normalizeQuoteDocumentActivity,
  normalizeQuoteDocumentActivityList,
  normalizeQuoteDocumentDetail,
  normalizeQuoteDocumentRevisionsList,
  normalizeQuoteDocumentsList,
} from '@/utils/normalizeQuoteDocument';
import {
  normalizePartsCatalogHit,
  uniquePartsCatalogHits,
} from '@/utils/partsCatalogHit';

import api, { handleError } from './axiosInstance';

export class QuoteDocumentsApiError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly currentDraftRevision?: number;

  constructor(
    message: string,
    status?: number,
    extras?: { code?: string; currentDraftRevision?: number }
  ) {
    super(message);
    this.name = 'QuoteDocumentsApiError';
    this.status = status;
    this.code = extras?.code;
    this.currentDraftRevision = extras?.currentDraftRevision;
  }
}

const listParams = (params: ListQuoteDocumentsParams) => ({
  ...(params.search ? { search: params.search } : {}),
  ...(params.status ? { status: params.status } : {}),
  ...(params.runStatus ? { run_status: params.runStatus } : {}),
  ...(params.jobRef ? { job_ref: params.jobRef } : {}),
  ...(params.jobGroupId ? { job_group_id: params.jobGroupId } : {}),
  ...(params.runId ? { runId: params.runId } : {}),
  ...(params.archived === true ? { archived: true } : {}),
  ...(params.page != null ? { page: params.page } : {}),
  ...(params.perPage != null ? { per_page: params.perPage } : {}),
});

const toApiError = (error: unknown, context: string): never => {
  const response =
    error && typeof error === 'object' && 'response' in error
      ? (
          error as {
            response?: {
              status?: number;
              data?: {
                message?: string;
                code?: string;
                current_draft_revision?: number;
              };
            };
          }
        ).response
      : undefined;

  if (response) {
    const message =
      response.data?.message ||
      (response.status === 403
        ? 'Access denied'
        : response.status === 409
          ? 'Draft revision conflict'
          : `${context} failed`);
    throw new QuoteDocumentsApiError(message, response.status, {
      code: response.data?.code,
      currentDraftRevision: response.data?.current_draft_revision,
    });
  }

  return handleError(error, context);
};

export const quoteDocumentsApi = {
  list: async (
    params: ListQuoteDocumentsParams = {},
    signal?: AbortSignal
  ): Promise<QuoteDocumentsListResponse | undefined> => {
    try {
      const { data } = await api.get(QUOTE_DOCUMENTS_API_BASE, {
        params: listParams(params),
        signal,
      });
      return normalizeQuoteDocumentsList(data);
    } catch (error) {
      toApiError(error, 'list quote documents');
    }
  },

  get: async (
    documentId: string,
    signal?: AbortSignal
  ): Promise<QuoteDocumentDetail | undefined> => {
    try {
      const { data } = await api.get(
        `${QUOTE_DOCUMENTS_API_BASE}/${documentId}`,
        { signal }
      );
      return normalizeQuoteDocumentDetail(data);
    } catch (error) {
      toApiError(error, 'get quote document');
    }
  },

  patch: async (
    documentId: string,
    payload: PatchQuoteDocumentPayload,
    signal?: AbortSignal
  ): Promise<QuoteDocumentDetail | undefined> => {
    try {
      const { data } = await api.patch(
        `${QUOTE_DOCUMENTS_API_BASE}/${documentId}`,
        payload,
        { signal }
      );
      return normalizeQuoteDocumentDetail(data);
    } catch (error) {
      toApiError(error, 'patch quote document');
    }
  },

  listActivity: async (
    documentId: string,
    params: ListQuoteDocumentActivityParams & {
      kind?: QuoteDocumentActivityKind;
    } = {},
    signal?: AbortSignal
  ): Promise<QuoteDocumentActivityListResponse | undefined> => {
    try {
      const { data } = await api.get(
        `${QUOTE_DOCUMENTS_API_BASE}/${documentId}/activity`,
        {
          params: {
            ...(params.page != null ? { page: params.page } : {}),
            ...(params.perPage != null ? { per_page: params.perPage } : {}),
            ...(params.kind ? { kind: params.kind } : {}),
          },
          signal,
        }
      );
      return normalizeQuoteDocumentActivityList(data);
    } catch (error) {
      toApiError(error, 'list quote document activity');
    }
  },

  /** Comments are activity rows with action `commented`; newest first. */
  listComments: async (
    documentId: string,
    params: ListQuoteDocumentActivityParams = {},
    signal?: AbortSignal
  ): Promise<QuoteDocumentActivityListResponse | undefined> =>
    quoteDocumentsApi.listActivity(
      documentId,
      { ...params, kind: 'comments' },
      signal
    ),

  addComment: async (
    documentId: string,
    comment: string,
    signal?: AbortSignal
  ): Promise<QuoteDocumentActivity | undefined> => {
    try {
      const { data } = await api.post<QuoteDocumentActivityWire>(
        `${QUOTE_DOCUMENTS_API_BASE}/${documentId}/comments`,
        { comment },
        { signal }
      );
      return data ? normalizeQuoteDocumentActivity(data) : undefined;
    } catch (error) {
      toApiError(error, 'add quote document comment');
    }
  },

  listRevisions: async (
    documentId: string,
    signal?: AbortSignal
  ): Promise<QuoteDocumentRevisionsListResponse | undefined> => {
    try {
      const { data } = await api.get(
        `${QUOTE_DOCUMENTS_API_BASE}/${documentId}/revisions`,
        { signal }
      );
      return normalizeQuoteDocumentRevisionsList(data);
    } catch (error) {
      toApiError(error, 'list quote document revisions');
    }
  },

  createVersion: async (
    documentId: string,
    payload: CreateQuoteDocumentVersionPayload,
    signal?: AbortSignal
  ): Promise<CreateQuoteDocumentVersionResult | undefined> => {
    try {
      const { data } = await api.post(
        `${QUOTE_DOCUMENTS_API_BASE}/${documentId}/versions`,
        payload,
        { signal }
      );
      return normalizeCreateQuoteDocumentVersionResult(data);
    } catch (error) {
      toApiError(error, 'create quote document version');
    }
  },

  approve: async (
    documentId: string,
    signal?: AbortSignal
  ): Promise<QuoteDocumentDetail | undefined> => {
    try {
      const { data } = await api.post(
        `${QUOTE_DOCUMENTS_API_BASE}/${documentId}/approve`,
        {},
        { signal }
      );
      return normalizeQuoteDocumentDetail(data);
    } catch (error) {
      toApiError(error, 'approve quote document');
    }
  },

  accept: async (
    documentId: string,
    payload: QuoteDocumentLifecyclePayload = {},
    signal?: AbortSignal
  ): Promise<QuoteDocumentDetail | undefined> => {
    try {
      const { data } = await api.post(
        `${QUOTE_DOCUMENTS_API_BASE}/${documentId}/accept`,
        payload.revision_id ? { revision_id: payload.revision_id } : {},
        { signal }
      );
      return normalizeQuoteDocumentDetail(data);
    } catch (error) {
      toApiError(error, 'accept quote document');
    }
  },

  decline: async (
    documentId: string,
    payload: QuoteDocumentLifecyclePayload = {},
    signal?: AbortSignal
  ): Promise<QuoteDocumentDetail | undefined> => {
    try {
      const { data } = await api.post(
        `${QUOTE_DOCUMENTS_API_BASE}/${documentId}/decline`,
        payload.revision_id ? { revision_id: payload.revision_id } : {},
        { signal }
      );
      return normalizeQuoteDocumentDetail(data);
    } catch (error) {
      toApiError(error, 'decline quote document');
    }
  },

  reopen: async (
    documentId: string,
    signal?: AbortSignal
  ): Promise<QuoteDocumentDetail | undefined> => {
    try {
      const { data } = await api.post(
        `${QUOTE_DOCUMENTS_API_BASE}/${documentId}/reopen`,
        {},
        { signal }
      );
      return normalizeQuoteDocumentDetail(data);
    } catch (error) {
      toApiError(error, 'reopen quote document');
    }
  },

  duplicate: async (
    documentId: string,
    signal?: AbortSignal
  ): Promise<QuoteDocumentDetail | undefined> => {
    try {
      const { data } = await api.post(
        `${QUOTE_DOCUMENTS_API_BASE}/${documentId}/duplicate`,
        {},
        { signal }
      );
      return normalizeQuoteDocumentDetail(data);
    } catch (error) {
      toApiError(error, 'duplicate quote document');
    }
  },

  archive: async (
    documentId: string,
    signal?: AbortSignal
  ): Promise<QuoteDocumentDetail | undefined> => {
    try {
      const { data } = await api.post(
        `${QUOTE_DOCUMENTS_API_BASE}/${documentId}/archive`,
        {},
        { signal }
      );
      return normalizeQuoteDocumentDetail(data);
    } catch (error) {
      toApiError(error, 'archive quote document');
    }
  },

  /** One save replaces the whole ordered line list. 409 on a stale draft revision. */
  replaceLines: async (
    documentId: string,
    payload: ReplaceQuoteDocumentLinesPayload,
    signal?: AbortSignal
  ): Promise<QuoteDocumentDetail | undefined> => {
    try {
      const { data } = await api.put(
        `${QUOTE_DOCUMENTS_API_BASE}/${documentId}/lines`,
        payload,
        { signal }
      );
      return normalizeQuoteDocumentDetail(data);
    } catch (error) {
      toApiError(error, 'replace quote document lines');
    }
  },
};

export const partsCatalogApi = {
  autocomplete: async (
    q: string,
    limit: number = PARTS_CATALOG_AUTOCOMPLETE_LIMIT,
    signal?: AbortSignal
  ): Promise<PartsCatalogHit[]> => {
    try {
      const { data } = await api.get<PartsCatalogHitWire[]>(
        PARTS_CATALOG_AUTOCOMPLETE_API,
        {
          params: { q, limit },
          signal,
        }
      );
      const rows = Array.isArray(data) ? data : [];
      return uniquePartsCatalogHits(
        rows.map(normalizePartsCatalogHit).filter(hit => hit.id.length > 0)
      );
    } catch (error) {
      return handleError(error, 'parts catalog autocomplete');
    }
  },
};
